import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTENT_ARGS,
  FLEX_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { CENTER } from '../../../utils/spds/constants/css-layout';
import { ROLE_RADIOGROUP } from '../../../utils/spds/constants/aria';
import { hash } from '@ember/helper';
import Button from './segmented-control/button.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Super from '../core/base.gjs';
import styles from './segmented-control.module.scss';

const METADATA = Metadata.define({
  description: `
    Used to pick one choice from a linear set of closely related choices, and immediately apply that selection

    ### Contextual component
    The buttons are exposed as a contextual component that can be used within the segmented control multiple times.
    HTML attributes can be set directly on the button to give it a \`value\`, and to set the initially selected button using the \`checked\` attribute.
  `,
  extends: [
    pick(ACTION_ARGS, ['onClick']),
    pick(COMPONENT_STATE_ARGS, [
      'disabled',
      { name: 'id', initialValue: 'styleguide-segmented-control' },
    ]),
    pick(CONTENT_ARGS, ['iconOnly']),
    pick(FLEX_ARGS, [
      { name: 'alignItems', default: CENTER },
      { name: 'gap', default: '2px' },
      { name: 'justifyContent', default: CENTER },
    ]),
  ],
  meta: {
    groupName: Arg.string({ description: 'Name attribute for the radio group' }).initial(
      'styleguide-segmented-control-group'
    ),
  },
  name: 'Segmented Control',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-Web-Components?node-id=18486-82323&node-type=INSTANCE',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/9684fc-segmented-control',
  },
});

export default class SpdsActionSegmentedControl extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-action-segmented-control';
  buttons = [];
  classesToMapToArgs = ['disabled', 'iconOnly'];
  styles = styles;

  get componentName() {
    return this.args.componentName ?? super.componentName;
  }

  get groupName() {
    return this.args.groupName ?? this.id;
  }

  registerButton = button => {
    this.buttons.push(button);
  };

  unregisterButton = id => {
    let index = this.buttons.findIndex(button => button.id === id);

    this.buttons.splice(index, 1);
  };

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  <template>
    <FlexRow
      @alignItems={{this.alignItems}}
      @fitContent={{true}}
      @gap={{this.gap}}
      @inline={{true}}
      @justifyContent={{this.justifyContent}}
      class={{this.classes}}
      data-test-spds-action-segmented-control-container
      id={{this.id}}
      role={{ROLE_RADIOGROUP}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Button=(component
            Button
            disabled=@disabled
            groupName=this.groupName
            onClick=@onClick
            registerComponent=this.registerButton
            unregisterComponent=this.unregisterButton
          )
        )
      }}
    </FlexRow>
  </template>
}

export const ACTION_SEGMENTED_CONTROL_METADATA = SpdsActionSegmentedControl.metadata;
